import React, { useState } from 'react';
import { Box, Typography, IconButton, Chip, Tooltip } from '@mui/material';
import { Play, Pause, Square, Clock } from 'lucide-react';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';

import { useAppSelector, useAppDispatch } from '@/store';
import {
  selectActiveTimers,
  pauseTimerAsync,
  resumeTimerAsync,
  stopTimerAsync,
  MAX_CONCURRENT_TIMERS,
} from '@/store/slices/timerSlice';
import { TimerStatus, ActiveTimer } from '@/types';
import { formatTimerSeconds, getTimerDisplaySeconds } from '@/shared/utils/timerDisplay';

/**
 * SingleTimerDisplay - Compact timer display for header bar
 */
interface SingleTimerDisplayProps {
  timer: ActiveTimer;
  index: number;
  totalTimers: number;
  onPauseResume: (taskId: string) => void;
  onStop: (taskId: string, taskTitle?: string) => void;
}

const SingleTimerDisplay: React.FC<SingleTimerDisplayProps> = ({
  timer,
  index,
  totalTimers,
  onPauseResume,
  onStop,
}) => {
  const timerIsRunning = timer.status === TimerStatus.RUNNING;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 0.5,
        borderRadius: 1,
        bgcolor: timerIsRunning ? 'rgba(22, 163, 74, 0.1)' : 'rgba(217, 119, 6, 0.1)',
        border: '1px solid',
        borderColor: timerIsRunning ? 'success.300' : 'warning.300',
      }}
    >
      {/* Timer badge with task name */}
      <Chip
        label={`#${index + 1}`}
        size="small"
        color={timerIsRunning ? 'success' : 'warning'}
        sx={{ height: 20, fontSize: '0.65rem', minWidth: 28 }}
      />

      {/* Task name */}
      <Tooltip title={timer.taskTitle || 'Task'}>
        <Typography
          variant="body2"
          sx={{
            maxWidth: 150,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.primary',
            fontWeight: 500,
          }}
        >
          {timer.taskTitle || 'Task'}
        </Typography>
      </Tooltip>

      {/* Timer display */}
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          fontWeight: 600,
          color: timerIsRunning ? 'success.700' : 'warning.700',
          minWidth: 60,
        }}
      >
        {formatTimerSeconds(getTimerDisplaySeconds(timer))}
      </Typography>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title={timerIsRunning ? 'Pause' : 'Resume'}>
          <IconButton
            size="small"
            onClick={() => onPauseResume(timer.taskId)}
            sx={{
              width: 28,
              height: 28,
              bgcolor: timerIsRunning ? 'warning.100' : 'success.100',
              '&:hover': {
                bgcolor: timerIsRunning ? 'warning.200' : 'success.200',
              },
            }}
          >
            {timerIsRunning ? (
              <Pause size={14} style={{ color: '#d97706' }} />
            ) : (
              <Play size={14} style={{ color: '#16a34a' }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Stop & Save">
          <IconButton
            size="small"
            onClick={() => onStop(timer.taskId, timer.taskTitle)}
            sx={{
              width: 28,
              height: 28,
              bgcolor: 'error.100',
              '&:hover': { bgcolor: 'error.200' },
            }}
          >
            <Square size={12} style={{ color: '#dc2626' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

/**
 * ActiveTimerBar Component
 * Persistent bar showing active timer(s) status
 * Now supports multiple timers (max 2)
 * Allows quick control without navigating away
 */
const ActiveTimerBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeTimers = useAppSelector(selectActiveTimers);
  const [stopConfirm, setStopConfirm] = useState<{ taskId: string; taskTitle?: string } | null>(null);

  // Don't render if no active timers
  if (activeTimers.length === 0) return null;

  const handlePauseResume = (taskId: string) => {
    const timer = activeTimers.find(t => t.taskId === taskId);
    if (timer?.status === TimerStatus.RUNNING) {
      dispatch(pauseTimerAsync(taskId));
    } else {
      dispatch(resumeTimerAsync(taskId));
    }
  };

  const handleStop = (taskId: string, _taskTitle?: string) => {
    setStopConfirm({ taskId, taskTitle: _taskTitle });
  };

  // Check if any timer is running for background color
  const anyRunning = activeTimers.some(t => t.status === TimerStatus.RUNNING);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 1,
        bgcolor: anyRunning ? 'success.50' : 'warning.50',
        borderBottom: '1px solid',
        borderColor: anyRunning ? 'success.200' : 'warning.200',
      }}
    >
      {/* Header with timer count */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Clock
          size={18}
          className={anyRunning ? 'timer-running' : ''}
          style={{ color: anyRunning ? '#16a34a' : '#d97706' }}
        />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: anyRunning ? 'success.700' : 'warning.700' }}
        >
          Active Timers
        </Typography>
        <Tooltip title={`You can run up to ${MAX_CONCURRENT_TIMERS} timers at the same time`}>
          <Chip
            label={`${activeTimers.length} running`}
            size="small"
            color={activeTimers.length >= MAX_CONCURRENT_TIMERS ? 'warning' : 'primary'}
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        </Tooltip>
      </Box>

      {/* Timer displays */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {activeTimers.map((timer, index) => (
          <SingleTimerDisplay
            key={timer.taskId}
            timer={timer}
            index={index}
            totalTimers={activeTimers.length}
            onPauseResume={handlePauseResume}
            onStop={handleStop}
          />
        ))}
      </Box>

      <ConfirmationModal
        open={!!stopConfirm}
        onClose={() => setStopConfirm(null)}
        data={
          stopConfirm
            ? {
                title: 'Stop timer',
                message: 'Stop the timer and log the time.',
                confirmText: 'Stop',
                type: 'warning',
                reasonInput: {
                  label: 'Reason',
                  placeholder: 'Why are you stopping this timer?',
                  required: true,
                },
                onConfirm: (reason) => {
                  if (stopConfirm) dispatch(stopTimerAsync({ taskId: stopConfirm.taskId, reason }));
                  setStopConfirm(null);
                },
              }
            : undefined
        }
      />
    </Box>
  );
};

export default ActiveTimerBar;
